import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AdminStatCard } from "./AdminStatCard";
import { toast } from "sonner";
import { Search, RefreshCw, Users, Ban, CalendarDays, Plus, X, Save, Trash2, Pencil, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminKlientet = () => {
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({ total: 0, blacklisted: 0, this_month: 0 });
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", id_number: "", notes: "" });

  useEffect(() => { loadClients(); loadStats(); }, []);

  const loadClients = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/clients?search=${search}&filter=${filter}`, { headers: { Authorization: `Bearer ${token}` } });
      setClients(res.data);
    } catch (e) { console.error(e); }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/clients/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadClients(); }, [search, filter]);

  const openModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      setForm({ name: client.name, email: client.email || "", phone: client.phone || "", id_number: client.id_number || "", notes: client.notes || "" });
    } else {
      setEditingClient(null);
      setForm({ name: "", email: "", phone: "", id_number: "", notes: "" });
    }
    setShowModal(true);
  };

  const saveClient = async () => {
    if (!form.name.trim()) { toast.error("Emri është i detyrueshëm"); return; }
    try {
      const token = localStorage.getItem("adminToken");
      if (editingClient) {
        await axios.put(`${API}/admin/clients/${editingClient.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Klienti u përditësua");
      } else {
        await axios.post(`${API}/admin/clients`, form, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Klienti u shtua");
      }
      setShowModal(false);
      loadClients();
      loadStats();
    } catch (e) { toast.error("Gabim"); console.error(e); }
  };

  const deleteClient = async (id) => {
    if (!window.confirm("Jeni të sigurt?")) return;
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/admin/clients/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Klienti u fshi");
      loadClients();
      loadStats();
    } catch (e) { toast.error("Gabim"); console.error(e); }
  };

  const toggleBlacklist = async (client) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(`${API}/admin/clients/${client.id}`, { blacklisted: !client.blacklisted }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(client.blacklisted ? "U hoq nga lista e zezë" : "U shtua në listën e zezë");
      loadClients();
      loadStats();
    } catch (e) { toast.error("Gabim"); console.error(e); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Klientët</h2>
        <Button onClick={() => openModal()} className="bg-yellow-600 hover:bg-yellow-500" data-testid="add-client-btn">
          <Plus className="h-4 w-4 mr-2" /> Shto Klient
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <AdminStatCard value={stats.total} label="Gjithsej" color="yellow" icon={<Users className="h-5 w-5" />} />
        <AdminStatCard value={stats.blacklisted} label="Lista e zezë" color="red" icon={<Ban className="h-5 w-5" />} active={filter === "blacklisted"} onClick={() => setFilter(filter === "blacklisted" ? "all" : "blacklisted")} />
        <AdminStatCard value={stats.this_month} label="Ky muaj" color="green" icon={<CalendarDays className="h-5 w-5" />} />
      </div>

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kërko sipas emrit, telefonit, ID..." className="bg-zinc-800 border-gray-700 text-white pl-10" data-testid="clients-search" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-zinc-800 border border-gray-700 text-white rounded-md px-3">
          <option value="all">Të gjithë klientët</option>
          <option value="blacklisted">Lista e zezë</option>
        </select>
        <Button variant="outline" onClick={() => { loadClients(); loadStats(); }} className="border-gray-700 text-gray-400">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {clients.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">Nuk u gjetën klientë</p>
          <p className="text-sm mt-1">Provoni të ndryshoni filtrat ose shtoni një klient të ri</p>
        </div>
      ) : (
        <div className="space-y-3">
          {clients.map((c) => (
            <div key={c.id} className={`bg-zinc-900 border rounded-xl p-4 hover:border-gray-700 transition-colors ${c.blacklisted ? "border-red-600/30" : "border-gray-800"}`} data-testid={`client-${c.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold">{c.name}</p>
                    {c.blacklisted && <span className="px-2 py-0.5 bg-red-600/20 text-red-400 rounded-full text-xs">Lista e zezë</span>}
                  </div>
                  <div className="flex gap-4 text-gray-400 text-sm mt-1">
                    {c.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone}</span>}
                    {c.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>}
                  </div>
                  {c.id_number && <p className="text-gray-500 text-xs mt-1">ID: {c.id_number}</p>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => toggleBlacklist(c)} className={c.blacklisted ? "text-green-500 hover:text-green-400" : "text-red-500 hover:text-red-400"}>
                    <Ban className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openModal(c)} className="text-gray-400 hover:text-white">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteClient(c.id)} className="text-red-500 hover:text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-white">{editingClient ? "Ndrysho Klientin" : "Shto Klient të Ri"}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <div><Label className="text-gray-300">Emri *</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" /></div>
              <div><Label className="text-gray-300">Email</Label><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" /></div>
              <div><Label className="text-gray-300">Telefoni</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" /></div>
              <div><Label className="text-gray-300">Nr. ID / Pasaportë</Label><Input value={form.id_number} onChange={(e) => setForm({...form, id_number: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" /></div>
              <div><Label className="text-gray-300">Shënime</Label><Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="bg-zinc-800 border-gray-700 text-white mt-1" rows={2} /></div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowModal(false)} className="border-gray-600 text-gray-300">Anulo</Button>
              <Button onClick={saveClient} className="bg-yellow-600 hover:bg-yellow-500"><Save className="h-4 w-4 mr-2" />Ruaj</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
